import { motion } from 'framer-motion';
import { useState } from 'react';
import { ResponsiveContainer, Treemap } from 'recharts';
import formatNumber from '../../utils/formatNumber';

const GENERATION_COLORS = {
  1: { fill: '#84C341', glow: 'rgba(132, 195, 65, 0.3)' },
  2: { fill: '#00C49F', glow: 'rgba(0, 196, 159, 0.3)' },
  3: { fill: '#0088FE', glow: 'rgba(0, 136, 254, 0.3)' },
  4: { fill: '#FFBB28', glow: 'rgba(255, 187, 40, 0.3)' },
  collab: { fill: '#FF8042', glow: 'rgba(255, 128, 66, 0.3)' },
};

function CustomTreemapContent({ x, y, width, height, name, value, gen, depth, index }) {
  if (width < 40 || height < 30 || !name) return null;

  const colors = GENERATION_COLORS[gen] || GENERATION_COLORS.collab;
  const isSmall = width < 80 || height < 50;
  const displayName = String(name || '');

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={4}
        fill={colors.fill}
        fillOpacity={0.15 + (depth === 1 ? 0.1 : 0)}
        stroke={colors.fill}
        strokeWidth={1}
        strokeOpacity={0.4}
        style={{
          filter: `drop-shadow(0 0 8px ${colors.glow})`,
          cursor: 'pointer',
        }}
      />

      <rect
        x={x + 2}
        y={y + 2}
        width={Math.max(0, width - 4)}
        height={3}
        rx={1.5}
        fill={colors.fill}
        fillOpacity={0.6}
      />

      {!isSmall && (
        <>
          <text
            x={x + width / 2}
            y={y + height / 2 - 6}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#e5e5e5"
            fontSize={Math.min(14, width / 8)}
            fontWeight="700"
            style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            {displayName.length > 15 ? `${displayName.slice(0, 12)}...` : displayName}
          </text>
          <text
            x={x + width / 2}
            y={y + height / 2 + 12}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={colors.fill}
            fontSize={Math.min(18, width / 6)}
            fontWeight="800"
          >
            {formatNumber(value)}
          </text>
        </>
      )}

      {isSmall && width >= 50 && height >= 35 && (
        <text
          x={x + width / 2}
          y={y + height / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={colors.fill}
          fontSize={12}
          fontWeight="700"
        >
          {formatNumber(value)}
        </text>
      )}
    </g>
  );
}

export default function CensusTreemap({ populationByGeneration }) {
  const [hoveredGen, setHoveredGen] = useState(null);

  if (!populationByGeneration || populationByGeneration.length === 0) return null;

  const treemapData = populationByGeneration
    .filter((gen) => gen.value > 0)
    .map((gen) => ({
      name: gen.name,
      value: gen.value,
      gen: gen.gen,
      children: gen.children
        ? gen.children.filter((c) => c.value > 0).map((child) => ({
            ...child,
            gen: gen.gen,
          }))
        : undefined,
    }));

  const totalPopulation = populationByGeneration.reduce((acc, gen) => acc + gen.value, 0);

  return (
    <section className="py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-8"
      >
        <h2 className="text-2xl font-black text-neutral-200 tracking-tight uppercase">
          Population by Generation
        </h2>
        <p className="text-neutral-500 mt-2">
          {formatNumber(totalPopulation)} total shitizens across all generations
        </p>
        <div className="mt-3 mx-auto w-16 h-1 rounded-full bg-gradient-to-r from-[#84C341] via-[#00C49F] to-[#FF8042]" />
      </motion.div>

      <div className="flex flex-wrap justify-center gap-2 lg:gap-4 mb-6">
        {populationByGeneration
          .filter((gen) => gen.value > 0)
          .map((gen, index) => {
            const colors = GENERATION_COLORS[gen.gen] || GENERATION_COLORS.collab;
            const percentage = ((gen.value / totalPopulation) * 100).toFixed(1);

            return (
              <motion.div
                key={gen.name}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
                className="flex items-center gap-1.5 lg:gap-2 px-2 lg:px-4 py-1.5 lg:py-2 rounded-lg"
                style={{
                  background: 'rgba(38,38,38,0.6)',
                  border: `1px solid ${hoveredGen === gen.gen ? colors.fill : 'rgba(255,255,255,0.06)'}`,
                  boxShadow: hoveredGen === gen.gen ? `0 0 20px ${colors.glow}` : 'none',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={() => setHoveredGen(gen.gen)}
                onMouseLeave={() => setHoveredGen(null)}
              >
                <div
                  className="w-2 lg:w-3 h-2 lg:h-3 rounded-full shrink-0"
                  style={{
                    backgroundColor: colors.fill,
                    boxShadow: `0 0 10px ${colors.glow}`,
                  }}
                />
                <span className="text-xs lg:text-sm font-semibold text-neutral-300">{gen.name}</span>
                <span className="text-xs lg:text-sm text-neutral-500 hidden sm:inline">{formatNumber(gen.value)}</span>
                <span
                  className="text-xs font-bold px-1.5 lg:px-2 py-0.5 rounded"
                  style={{
                    backgroundColor: `${colors.fill}20`,
                    color: colors.fill,
                  }}
                >
                  {percentage}%
                </span>
              </motion.div>
            );
          })}
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(38,38,38,0.5) 0%, rgba(23,23,23,0.8) 100%)',
          border: '1px solid rgba(255,255,255,0.06)',
          padding: '1rem',
        }}
      >
        <div className="h-[280px] sm:h-[350px] lg:h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={treemapData}
              dataKey="value"
              aspectRatio={4 / 3}
              stroke="rgba(0,0,0,0.3)"
              content={<CustomTreemapContent />}
              isAnimationActive={false}
            />
          </ResponsiveContainer>
        </div>
      </motion.div>
    </section>
  );
}
